import { Classes, Colors, Dialog, Divider, Icon, IconName, InputGroup, Spinner } from "@blueprintjs/core";
import classnames from "classnames";
import { getIn } from "formik";
import {
  baseReturnType,
  baseSearchObject,
  DataTable,
  FGFormGroup,
  IDataTableColumn,
  IFGFormGroupProps,
  useFGContext,
  useGridState,
  useSearchApi,
} from "nsitools-react";
import * as React from "react";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";

export interface IFGAdvancedSearchInputProps<T> extends Omit<IFGFormGroupProps, "children"> {
  displayField?: string;
  valueField?: string;
  columns?: IDataTableColumn<T>[];
  searchFn: (sObj: baseSearchObject) => Promise<baseReturnType>;
  getDisplayedDataFn: (formValue: any) => Promise<T>;
  readonly?: boolean;
  round?: boolean;
  disabled?: boolean;
  leftIcon?: IconName;
  parseValueFieldAsNumber?: boolean;
  showTableHeader?: boolean;
  viewitemToolTip?: string;
  onItemViewClick?: (value: any) => void;
  isClearable?: boolean;
  placeholder?: string;
  setUndefinedOnClear?: boolean;
  backgroundColor?: string;
  sortKeys?: any;
  rightElement?: React.ReactElement;
}

const DataTableContainer = styled.div`
  tbody tr:hover {
    /* font-weight: bold */
    text-shadow: 0px 0px 1px black !important;
  }
`;

const ClearIcon = styled(Icon)`
  &:hover {
    color: ${Colors.RED1};
  }
`;

const ViewIcon = styled(Icon)`
  &:hover {
    color: ${Colors.BLUE1};
  }
`;

const ButtonTextContainer = styled.div`
  & > * + * {
    margin-left: 1rem;
  }

  & .placeholder-text {
    color: #b3aab1 !important;
  }
`;
const StyledSpinner = styled(Spinner)`
  z-index: 10 !important;
`;

const SelectContainer = styled.div`
  .${Classes.BUTTON} {
    justify-content: space-between;
  }

  & .${Classes.ROUND} {
    border-radius: 30px;
  }
`;

export function FGAdvancedSearchInput<T>({
  displayField = "displayValue",
  valueField = "idValue",
  columns,
  sortKeys,
  backgroundColor,
  searchFn,
  getDisplayedDataFn,
  readonly,
  leftIcon,
  placeholder,
  round = false,
  parseValueFieldAsNumber = true,
  showTableHeader = false,
  onItemViewClick,
  viewitemToolTip,
  disabled,
  isClearable = true,
  setUndefinedOnClear,
  rightElement,
  ...formGroupProps
}: IFGAdvancedSearchInputProps<T>) {
  const { fill, formik, editMode, disabled: globalDisabled, large } = useFGContext();

  const finalDisabled = React.useMemo(() => globalDisabled || disabled, [disabled, globalDisabled]);

  const { t } = useTranslation();

  // LoadingSingleValue
  const [loadingSingleValue, setLoadingSingleValue] = React.useState(false);
  const [currentDisplayedItem, setCurrentDisplayedItem] = React.useState<T>(null);
  const [loadingSingleValueError, setLoadingSingleValueError] = React.useState(false);

  const idValue = React.useMemo(() => getIn(formik.values, formGroupProps.name), [formGroupProps.name, formik.values]);

  React.useEffect(() => {
    if (!currentDisplayedItem && idValue && !loadingSingleValue && !loadingSingleValueError) {
      setLoadingSingleValue(true);
      getDisplayedDataFn(idValue)
        .then((result) => {
          setCurrentDisplayedItem(result);
          setLoadingSingleValue(false);
        })
        .catch((error) => {
          console.error("Unable to get single value item", error);
          setLoadingSingleValueError(true);
          setLoadingSingleValue(false);
        });
    }
  }, [
    currentDisplayedItem,
    formGroupProps.name,
    formik.values,
    getDisplayedDataFn,
    idValue,
    loadingSingleValue,
    loadingSingleValueError,
  ]);
  const finalDisplayedValue = React.useMemo(
    () => currentDisplayedItem && getIn(currentDisplayedItem, displayField),
    [currentDisplayedItem, displayField]
  );

  const gridstate = useGridState(
    sortKeys
      ? {
          enableFilter: true,
          pageSize: 10,
          sortKeys: sortKeys,
          serverMode: true,
        }
      : { enableFilter: true, pageSize: 10, serverMode: true }
  );

  const { loading, search } = useSearchApi(
    React.useMemo(
      () => ({
        searchFunction: searchFn,
        tableState: gridstate,
        initialSearch: false,
        trimSearchObjectStringValues: true,
      }),
      [gridstate, searchFn]
    )
  );

  const finalColumns = React.useMemo<IDataTableColumn<T>[]>(
    () =>
      columns || [
        {
          fieldName: displayField,
          autoFitContent: true,
        },
      ],
    [columns, displayField]
  );

  const [dialogOpened, setDialogOpened] = React.useState(false);
  const selectItem = React.useCallback(
    async (item: T) => {
      formik.setFieldTouched(formGroupProps.name, true, false);
      const idValueFromFormik = getIn(item, valueField);

      await formik.setFieldValue(
        formGroupProps.name,
        parseValueFieldAsNumber ? parseInt(idValueFromFormik) : idValueFromFormik,
        true
      );
      setCurrentDisplayedItem(item);
      setDialogOpened(false);
    },
    [formGroupProps.name, formik, parseValueFieldAsNumber, valueField]
  );

  const clearValue = React.useCallback(
    (e: any) => {
      e.stopPropagation();
      formik.setFieldTouched(formGroupProps.name, true, false);
      formik.setFieldValue(formGroupProps.name, setUndefinedOnClear ? undefined : null);
      setCurrentDisplayedItem(null);
    },
    [formGroupProps.name, formik, setUndefinedOnClear]
  );

  const viewItemClick = React.useCallback(
    (e: any) => {
      e.stopPropagation();
      onItemViewClick(getIn(formik.values, formGroupProps.name));
    },
    [formGroupProps.name, formik.values, onItemViewClick]
  );

  const hasValidData = React.useMemo(
    () => currentDisplayedItem !== null && currentDisplayedItem !== undefined,
    [currentDisplayedItem]
  );

  return (
    <>
      <FGFormGroup {...formGroupProps}>
        {({ intent, name }) =>
          editMode && !readonly ? (
            <SelectContainer>
              <button
                onClick={() => {
                  if (!finalDisabled) setDialogOpened(true);
                }}
                type="button"
                style={!finalDisabled ? { backgroundColor } : {}}
                className={classnames(Classes.BUTTON, Classes.FILL, {
                  [`bp4-intent-${intent.toLowerCase()}`]: !!intent && intent !== "none",
                  [Classes.DISABLED]: finalDisabled,
                  [Classes.OUTLINED]: !finalDisabled,
                  [Classes.ROUND]: round,
                })}
              >
                {loadingSingleValue ? (
                  <StyledSpinner size={16} />
                ) : (
                  <>
                    <ButtonTextContainer>
                      {leftIcon && <Icon icon={leftIcon} />}
                      <span className={classnames(Classes.BUTTON_TEXT)}>
                        {finalDisplayedValue || (
                            <span
                              className={classnames({
                                "placeholder-text": !!placeholder,
                              })}
                            >
                              {placeholder}
                            </span>
                          ) ||
                          t("FGSelectInput_SelectAnItem")}
                      </span>
                    </ButtonTextContainer>
                    <span style={{ display: "inline-flex" }}>
                      {hasValidData && (
                        <>
                          {isClearable && !disabled && <ClearIcon icon="small-cross" onClick={clearValue} />}
                          &nbsp;
                          {onItemViewClick && <ViewIcon icon="eye-open" onClick={viewItemClick} />}
                          {rightElement}
                          <Divider />
                        </>
                      )}
                      <Icon icon="search" />
                    </span>
                  </>
                )}
              </button>
            </SelectContainer>
          ) : onItemViewClick ? (
            <div style={{ padding: "6px 10px", display: "flex" }}>
              <a
                href="_"
                onClick={(e) => {
                  e.preventDefault();
                  viewItemClick(e);
                }}
              >
                {finalDisplayedValue || ""}
              </a>
              <div style={{ marginLeft: "1rem" }}>{rightElement}</div>
            </div>
          ) : (
            <InputGroup
              leftIcon={leftIcon}
              round={round}
              large={large}
              name={name}
              intent={intent}
              value={finalDisplayedValue || ""}
              fill={fill}
              style={{ boxShadow: "none", backgroundColor: "transparent" }}
              rightElement={rightElement}
              readOnly
            />
          )
        }
      </FGFormGroup>
      <Dialog
        style={{
          minWidth: 1000,
          backgroundColor: "white",
          overflowX: "auto",
          paddingBottom: 0,
        }}
        isOpen={dialogOpened}
        isCloseButtonShown={true}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        title={formGroupProps.label}
        onClose={() => setDialogOpened(false)}
        onOpened={() => search()}
      >
        <DataTableContainer className={Classes.DIALOG_BODY}>
          <DataTable
            loading={loading}
            onRowClick={selectItem}
            columns={finalColumns}
            tableState={gridstate}
            showHeader={showTableHeader}
            showPageSizeSelector={false}
            customizeRowStyle={() => css`
              white-space: nowrap;
            `}
            filterMode="OnEnter"
            focusOnFilter={true}
          />
        </DataTableContainer>
      </Dialog>
    </>
  );
}
